import { useCallback } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import { SheetHeader } from '@/components/shared/shop/ui/SheetHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ManageListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Kept for parity with previous API; header now uses a fixed copy from locales. */
  listName?: string;
  onEditName: () => void;
  onDeleteList: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Manage list sheet shown from the Profile > Favorites list detail header.
 * EDIT / DELETE / CANCEL actions.
 */
export function ManageListSheet({ open, onOpenChange, onEditName, onDeleteList }: ManageListSheetProps) {
  const t = useClientTranslation();

  const handleCancel = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleEdit = useCallback(() => {
    onEditName();
  }, [onEditName]);

  const handleDelete = useCallback(() => {
    onDeleteList();
  }, [onDeleteList]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col">
        <SheetHeader title={t('App.Shop.Wishlist.ManageList')} />

        {/* Edit / Delete / Cancel actions */}
        <div className="flex flex-col gap-3 px-4 pt-2 pb-10.5">
          <Button variant="primary-solid" size="lg" onClick={handleEdit} className="w-full">
            {t('App.Shop.Collection.EditListName')}
          </Button>
          <Button variant="secondary-outline" size="lg" onClick={handleDelete} className="w-full">
            {t('App.Shop.Collection.DeleteList')}
          </Button>
          <Button variant="tertiary-link" size="md" onClick={handleCancel} className="w-full">
            {t('App.Labels.Cancel')}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
